import React, { useState, useEffect } from "react";
import { Container, Card, Table, Badge, Button, Row, Col, Spinner, Form } from "react-bootstrap";
import { FaBook, FaSearch, FaEye, FaFilePdf, FaReply, FaCopy, FaCheckCircle, FaExclamationCircle, FaClock, FaBoxes } from "react-icons/fa";
import { getFirestore, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import "jspdf-autotable";

const db = getFirestore(firebaseApp);

function GestionLibroReclamaciones({ usuario }) {
  const [loading, setLoading] = useState(false);
  const [reclamaciones, setReclamaciones] = useState([]);
  const ruc = usuario.ruc;

  useEffect(() => {
    if (ruc) {
      cargarReclamaciones();
    }
  }, [ruc]);

  const cargarReclamaciones = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "reclamaciones"),
        where("ruc_empresa", "==", ruc),
        orderBy("num_hoja", "desc")
      );
      const snap = await getDocs(q);
      const data = [];
      snap.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() });
      });
      setReclamaciones(data);
    } catch (error) {
      console.error("Error al cargar reclamaciones:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/p/reclamaciones?ruc=${ruc}`;
    navigator.clipboard.writeText(url);
    Swal.fire({
      icon: 'success',
      title: 'Enlace Copiado',
      text: 'El link para el libro público ha sido copiado al portapapeles.',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const stats = {
    total: reclamaciones.length,
    pendientes: reclamaciones.filter(r => r.estado === "Pendiente").length,
    atendidos: reclamaciones.filter(r => r.estado === "Atendido").length
  };

  const generarPDF = (rec) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Encabezado
    doc.setFontSize(18);
    doc.setTextColor(33, 37, 41);
    doc.text("HOJA DE RECLAMACIÓN", pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(10);
    doc.text(`N° Correlativo: H-${String(rec.num_hoja).padStart(3, '0')}`, pageWidth - 20, 20, { align: "right" });
    
    doc.setLineWidth(0.5);
    doc.line(20, 25, pageWidth - 20, 25);

    // Datos de la Empresa
    doc.setFont("helvetica", "bold");
    doc.text("1. IDENTIFICACIÓN DEL PROVEEDOR", 20, 35);
    doc.setFont("helvetica", "normal");
    doc.autoTable({
      startY: 38,
      head: [["RUC", "Razón Social", "Fecha de Registro"]],
      body: [[rec.ruc_empresa, usuario?.empresa || "-", rec.fecha?.toDate() ? rec.fecha.toDate().toLocaleString() : "-"]],
      theme: "grid",
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] }
    });

    // Datos del Cliente
    doc.setFont("helvetica", "bold");
    doc.text("2. IDENTIFICACIÓN DEL CONSUMIDOR", 20, doc.lastAutoTable.finalY + 10);
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 13,
      body: [
        ["Nombre:", rec.cliente_nombre],
        ["Documento:", rec.cliente_dni],
        ["Email:", rec.cliente_email],
        ["Teléfono:", rec.cliente_telefono || "-"]
      ],
      theme: "plain",
      styles: { cellPadding: 1 }
    });

    // Detalle de la Reclamación
    doc.setFont("helvetica", "bold");
    doc.text("3. DETALLE DE LA RECLAMACIÓN Y PEDIDO", 20, doc.lastAutoTable.finalY + 10);
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 13,
      head: [["Tipo", "Detalle de la Incidencia"]],
      body: [[rec.tipo, rec.detalle]],
      theme: "grid",
      headStyles: { fillColor: [124, 58, 237], textColor: [255, 255, 255] }
    });

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 5,
      head: [["Pedido del Consumidor"]],
      body: [[rec.pedido]],
      theme: "grid",
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] }
    });

    // Pie de página
    doc.setFontSize(8);
    doc.setTextColor(150);
    const footerText = "Este es un documento generado por el sistema de Gestión de Reclamaciones de Mi Boleta.";
    doc.text(footerText, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });

    doc.save(`Reclamacion_H${String(rec.num_hoja).padStart(3, '0')}.pdf`);
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        padding: "20px 0",
      }}
    >
      <Container fluid className="px-lg-5 px-3 pb-5">
        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden", minHeight: "85vh" }}
        >
          {/* Header Premium Estilo CatalogoEPPs */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
              borderBottom: "3px solid #6d28d9",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1 d-flex align-items-center justify-content-center">
                <FaBook className="me-3" />
                Libro de Reclamaciones
              </h2>
              <p className="mb-0 opacity-75">
                Gestión de quejas y reclamos de clientes externos
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con acciones */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                        color: "white",
                      }}
                    >
                      <FaBook size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Acciones Administrativas</h5>
                    <p className="text-muted mb-0 small">
                      Gestione el libro público y comparta el enlace con sus clientes
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <Button
                      variant="light"
                      onClick={copyPublicLink}
                      className="d-flex align-items-center gap-2 ms-auto"
                      style={{ borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
                    >
                      <FaCopy size="16px" /> Copiar Enlace Público
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Fila de KPIs Estilo CatalogoEPPs */}
            <Row className="mb-4 g-3">
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.total}</h4>
                    <p className="mb-0 opacity-90 small">Total Registros</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.pendientes}</h4>
                    <p className="mb-0 opacity-90 small">Pendientes de Atención</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.atendidos}</h4>
                    <p className="mb-0 opacity-90 small">Atendidos / Resueltos</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Tabla de Resultados Estilo Premium */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                  border: "none"
                }}
              >
                <div className="d-flex align-items-center">
                  <FaBoxes className="me-2" />
                  <div className="flex-grow-1">
                    <h5 className="mb-0 fw-semibold">Registros del Libro</h5>
                    <small className="opacity-75">Visualización detallada de incidencias</small>
                  </div>
                  <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                    {reclamaciones.length} Registros
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                  <Table hover className="align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                    <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0, zIndex: 10 }}>
                      <tr>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Hoja N°</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Consumidor</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Tipo</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold">Incidencia</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Estado</th>
                        <th className="py-3 px-3 border-0 text-secondary fw-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                          </td>
                        </tr>
                      ) : reclamaciones.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <FaBook size="4rem" className="text-muted mb-3 opacity-50" />
                            <h5 className="text-muted">No se encontraron registros</h5>
                            <p className="text-muted small">Los registros aparecerán aquí cuando los clientes envíen el formulario público.</p>
                          </td>
                        </tr>
                      ) : (
                        reclamaciones.map((rec) => (
                          <tr key={rec.id}>
                            <td className="py-3 px-3 text-center fw-bold text-dark">
                              {String(rec.num_hoja).padStart(3, '0')}
                            </td>
                            <td className="py-3 px-3">
                              <div className="fw-bold text-dark">{rec.cliente_nombre}</div>
                              <small className="text-muted">{rec.cliente_dni} • {rec.cliente_email}</small>
                            </td>
                            <td className="py-3 px-3">
                              <Badge bg={rec.tipo === "Reclamación" ? "warning" : "info"} className="text-capitalize" style={{ fontWeight: "500" }}>
                                {rec.tipo}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <div className="text-truncate" style={{ maxWidth: "250px" }}>{rec.detalle}</div>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <Badge
                                bg={rec.estado === "Pendiente" ? "danger" : "success"}
                                pill
                                style={{ fontSize: "0.85rem", minWidth: "100px" }}
                              >
                                {rec.estado}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <div className="d-flex gap-2 justify-content-center">
                                <Button variant="outline-primary" size="sm" title="Ver Detalle" style={{ borderRadius: "6px" }}><FaEye /></Button>
                                <Button variant="outline-success" size="sm" title="Responder" style={{ borderRadius: "6px" }}><FaReply /></Button>
                                <Button variant="outline-danger" size="sm" title="Exportar PDF" onClick={() => generarPDF(rec)} style={{ borderRadius: "6px" }}><FaFilePdf /></Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default GestionLibroReclamaciones;
